import { TypeSystem } from "../../support/type-system.support";
import { OrderEventOriginType } from "../dto/order-event-origin-type.enum";
import { OrderEventStatus } from "../dto/order-event-status.enum";
import { OrderEventItemForGet } from "./order-event-item-for-get.model";
import { OrderEventProductsForGet } from "./order-event-products-for-get.model";
import { OrderEventTrackingForGet } from "./order-event-tracking-for-get.model";
import { ReplacementItemForGet } from "./replacement-item-for-get.model";

	/**
	 * @description
	 *  Modelo de una nota de CyR (cambio y reclamo), o NC (nota de crédito),
	 *	sobre un pedido. Este modelo es un DTO para el frotnend, a diferencia
	 *	de la otra versión (../dto/order-event.model).
	 *
	 * @see gigot-backend/ViewModels/OrderEvent/OrderEventForGetDto.cs
	 */

export class OrderEventForGet {

	public active: boolean;
	public applicationUserId: string;
	public applicationUserName: string;
	public creationDate: Date;
	public generateCreditNote: boolean;
	public id: number;
	public orderEventItems: OrderEventItemForGet[];
	public orderEventOriginType: OrderEventOriginType;
	public orderEventProducts: OrderEventProductsForGet[];
	public orderEventResultId: number;
	public orderEventStatus: OrderEventStatus;
	public orderEventTrackings: OrderEventTrackingForGet[];
	public orderEventTypeId: number;
	public orderId: number;
	public replacementItems: ReplacementItemForGet[];

	/**
	 * @description
	 *	Construye una nota con una semántica vacía. Nótese que no es lo mismo
	 *	que construir una nota con valores por defecto. Las reglas generales
	 *	de construcción son:
	 *
	 *      - Campos 'array' se definen como [].
	 *      - Campos 'boolean' se definen como 'false'.
	 *      - Campos 'Date' se definen como 'undefined'.
	 *      - Campos 'enum' se definen como 'undefined'.
	 *      - Campos 'number' se definen como 'undefined'.
	 *      - Campos 'string' se definen como '' (cadena vacía).
	 *      - Campos 'object' se definen llamando a su propio método #empty().
	 *      - Campos de la superclase se definen al igual que campos 'object'.
	 *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
	 *
	 * @returns
	 *	Una nota de CyR/NC vacía.
	 */
	public static empty(): OrderEventForGet {
		const orderEventForGet: OrderEventForGet = new OrderEventForGet();
		orderEventForGet.active = false;
		orderEventForGet.applicationUserId = "";
		orderEventForGet.applicationUserName = "";
		orderEventForGet.creationDate = undefined;
		orderEventForGet.generateCreditNote = false;
		orderEventForGet.id = undefined;
		orderEventForGet.orderEventItems = [];
		orderEventForGet.orderEventOriginType = undefined;
		orderEventForGet.orderEventProducts = [];
		orderEventForGet.orderEventResultId = undefined;
		orderEventForGet.orderEventStatus = undefined;
		orderEventForGet.orderEventTrackings = [];
		orderEventForGet.orderEventTypeId = undefined;
		orderEventForGet.orderId = undefined;
		orderEventForGet.replacementItems = [];
		return orderEventForGet;
	}

	/**
	 * @description
	 *  Método de reificación de un modelo compatible anémico. Esto permite
	 *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
	 *  valor de sus propiedades se mantiene.
	 *
	 * @param anemicOrderEventForGet
	 *  El modelo anémico a reificar.
	 *
	 * @returns
	 *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
	 *  profundidad desde el modelo anémico.
	 */
	public static from(anemicOrderEventForGet: OrderEventForGet): OrderEventForGet {
		if (!anemicOrderEventForGet) return undefined;
		const orderEventForGet: OrderEventForGet = OrderEventForGet.empty();
		orderEventForGet.active = anemicOrderEventForGet.active;
		orderEventForGet.applicationUserId = anemicOrderEventForGet.applicationUserId;
		orderEventForGet.applicationUserName = anemicOrderEventForGet.applicationUserName;
		orderEventForGet.creationDate = new Date(anemicOrderEventForGet.creationDate);
		orderEventForGet.generateCreditNote = anemicOrderEventForGet.generateCreditNote;
		orderEventForGet.id = anemicOrderEventForGet.id;
		orderEventForGet.orderEventItems = TypeSystem.orElse(anemicOrderEventForGet.orderEventItems, [])
			.map(orderEventItem => OrderEventItemForGet.from(orderEventItem));
		orderEventForGet.orderEventOriginType = anemicOrderEventForGet.orderEventOriginType;
		orderEventForGet.orderEventProducts = TypeSystem.orElse(anemicOrderEventForGet.orderEventProducts, [])
			.map(orderEventProduct => OrderEventProductsForGet.from(orderEventProduct));
		orderEventForGet.orderEventResultId = anemicOrderEventForGet.orderEventResultId;
		orderEventForGet.orderEventStatus = anemicOrderEventForGet.orderEventStatus;
		orderEventForGet.orderEventTrackings = TypeSystem.orElse(anemicOrderEventForGet.orderEventTrackings, [])
			.map(orderEventTracking => OrderEventTrackingForGet.from(orderEventTracking));
		orderEventForGet.orderEventTypeId = anemicOrderEventForGet.orderEventTypeId;
		orderEventForGet.orderId = anemicOrderEventForGet.orderId;
		orderEventForGet.replacementItems = TypeSystem.orElse(anemicOrderEventForGet.replacementItems, [])
			.map(replacementItem => ReplacementItemForGet.from(replacementItem));
		return orderEventForGet;
	}
}
